import { Box, Button, Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { BUTTONS } from "../../constants";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Card } from "react-bootstrap";
import { AppointmentServiceInternal } from "../../services/appointment.service";
import inmateinformation from "../../assets/images/InmateInformation-logo.png";

interface InterfaceProps {
  changeStep: (data: any) => void;
  updateFormData: (data: any) => void;
  formData: any;
}
interface CountyOption {
  countyid: number;
  countyname: string;
}

const InmateInfoForm: React.FC<InterfaceProps> = (props: any) => {
  const [heldForCounty, setHeldForAnotherCounty] = useState(false);
  const [countyOptions, setCountyOptions] = useState<CountyOption[]>([]);
  const [selectedCounty, setSelectedCounty] = useState<CountyOption | null>(null);
  const [selectedAdminCounty, setSelectedAdminCounty] = useState<
    CountyOption[]
  >([]);

  const fetchDataFromApi = async () => {
    try {
      const response = await AppointmentServiceInternal.county();
      if (response?.data.isSuccess) {
        setCountyOptions(response.data.data);
        setSelectedAdminCounty(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching data from API:", error);
    }
  };

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      inmateFirstName: "",
      inmateLastName: "",
      inmatedob: "",
      // ZipCode: "",
      heldforanothercounty: heldForCounty,
      countyid: "",
      admincountyid: "",
      additionalinfo: "",
      medications: "",
      incarceration_date: "",
      anticipated_release_date: "",
    },
    resolver: yupResolver(
      yup.object().shape({
        inmateFirstName: yup.string().required("Inmate First Name is required"),
        inmateLastName: yup.string().required("Inmate Last Name is required"),
        inmatedob: yup.string().required("Date of birth is required"),
        // ZipCode: yup.string().required("ZipCode is required"),
        heldforanothercounty: yup.boolean().required("This field is required"),
        countyid: yup.string().when(["heldforanothercounty"], ([heldforanothercounty], schema) =>
          heldforanothercounty ?
          schema.required("This field is required") :
          schema.notRequired()
        ),
        admincountyid: yup.string().notRequired(),
        additionalinfo: yup.string().notRequired(),
        medications: yup.string().notRequired(),
        incarceration_date: yup.string().required("Date of incarceration is required"),
        anticipated_release_date: yup
          .string()
          .notRequired()
          .test(
            "release-not-before-incarceration",
            "Anticipated release date cannot be before the date of incarceration",
            function (value) {
              const { incarceration_date } = this.parent;
              if (!value || !incarceration_date) return true;
              return value >= incarceration_date;
            }
          ),
      })
    ),
  });

  const onHeldForChange  = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    if (!isChecked) {
      setSelectedCounty(null);
    }
    setValue("countyid", "");
    setValue("heldforanothercounty", isChecked);
    setHeldForAnotherCounty(isChecked);
  };

  const onCountyChange  = (event: React.ChangeEvent<{ value: unknown }>) => {
    const county = countyOptions.find(c => c.countyid === event.target.value as number);
    if (county) {
      setSelectedCounty(county);
    }
  };

  const onSubmit = async (data: any) => {
    const selectedAdminCountyId = Number(data.admincountyid);
    const AdminCountyName = selectedAdminCounty.find(
      (option: any) => option.countyid === selectedAdminCountyId
    );

    const combinedData = {
      ...data,
      heldforanothercounty: heldForCounty,
      countyid: heldForCounty ?
        selectedCounty?.countyid :
        0,
      countyname: heldForCounty ?
        selectedCounty?.countyname : 
        "",
      admincountyname: AdminCountyName?.countyname,
    };
    props.updateFormData(combinedData);
    props.changeStep(1);
  };

  useEffect(() => {
    fetchDataFromApi();
    setValue("inmateFirstName", props.formData.inmateFirstName);
    setValue("inmateLastName", props.formData.inmateLastName);
    setValue("inmatedob", props.formData.inmatedob);
    // setValue("ZipCode", props.formData.ZipCode);
    setValue("countyid", props.formData.countyid === 0 ? "" : props.formData.countyid);
    setValue("admincountyid", props.formData.admincountyid);
    setValue("medications", props.formData.medications);
    setValue("additionalinfo", props.formData.additionalinfo);
    setValue("heldforanothercounty", props.formData.heldforanothercounty);
    setValue("incarceration_date", props.formData.incarceration_date);
    setValue("anticipated_release_date", props.formData.anticipated_release_date);
    setHeldForAnotherCounty(props.formData.heldforanothercounty);
  }, [props.formData.ZipCode, props.formData.additionalinfo, props.formData.admincountyid, props.formData.countyid, props.formData.inmatedob, props.formData.inmatename, props.formData.heldforanothercounty, props.formData.medications, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="add-patient-form">
      <div className="d-flex justify-content-center">
        <Card
          style={{
            width: "100%",
            maxHeight: "calc(100vh - 113px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Card.Body style={{ flex: 1, overflowY: "auto" }}>
            <Card.Title>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box>
                    <img
                      alt=""
                      src={inmateinformation}
                      style={{ height: 32, width: 32 }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{ fontWeight: "bold" }}
                  >
                    Inmate Information
                  </Typography>
                </Stack>
              </Box>
            </Card.Title>
            <div className="patient-info">
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <div>
                    <div className="form-label">
                      <label>
                        Inmate's First Name
                        <span className="text-danger">*</span>
                      </label>
                    </div>
                    <Controller
                      name="inmateFirstName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          margin="normal"
                          type="text"
                          className="m-auto"
                          fullWidth
                        />
                      )}
                    />
                    {errors.inmateFirstName && (
                      <p className="text-red-600 text-sm">
                        {errors.inmateFirstName.message}
                      </p>
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} md={4}>
                  <div>
                    <div className="form-label">
                      <label>
                        Inmate's Last Name<span className="text-danger">*</span>
                      </label>
                    </div>
                    <Controller
                      name="inmateLastName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          margin="normal"
                          type="text"
                          className="m-auto"
                          fullWidth
                        />
                      )}
                    />
                    {errors.inmateLastName && (
                      <p className="text-red-600 text-sm">
                        {errors.inmateLastName.message}
                      </p>
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} md={4}>
                  <div>
                    <div className="form-label">
                      <label>
                        DOB<span className="text-danger">*</span>
                      </label>
                    </div>
                    <Controller
                      name="inmatedob"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          margin="normal"
                          type="date"
                          className="m-auto"
                          fullWidth
                        />
                      )}
                    />
                    {errors.inmatedob && (
                      <p className="text-red-600 text-sm">
                        {errors.inmatedob.message}
                      </p>
                    )}
                  </div>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <div>
                    <div className="form-label">
                      <label>
                        Date of Incarceration
                        <span className="text-danger">*</span>
                      </label>
                    </div>
                    <Controller
                      name="incarceration_date"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          margin="normal"
                          type="date"
                          className="m-auto"
                          fullWidth
                        />
                      )}
                    />
                    {errors.incarceration_date && (
                      <p className="text-red-600 text-sm">
                        {errors.incarceration_date.message}
                      </p>
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} md={4}>
                  <div>
                    <div className="form-label">
                      <label>Anticipated Release Date</label>
                    </div>
                    <Controller
                      name="anticipated_release_date"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          margin="normal"
                          type="date"
                          className="m-auto"
                          fullWidth
                        />
                      )}
                    />
                    {errors.anticipated_release_date && (
                      <p className="text-red-600 text-sm">
                        {errors.anticipated_release_date.message}
                      </p>
                    )}
                  </div>
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6} style={{ display: 'none' }}>
                  <div className="form-label">
                    <Controller
                      name="heldforanothercounty"
                      control={control}
                      defaultValue={props.formData.isHeldFor}
                      render={({ field }) => (
                        <FormControlLabel
                          label="Is inmate held for another county?"
                          disableTypography
                          control={
                            <Checkbox
                              {...field}
                              checked={false}
                              onChange={onHeldForChange}
                              size="small"
                            />
                          }
                        />
                      )}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <div
                    style={{ visibility: heldForCounty ? "visible" : "hidden" }}
                  >
                    <Controller
                      name="countyid"
                      control={control}
                      defaultValue={props.formData.countyid}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" margin="normal">
                          <InputLabel id="countyLabel">
                            County <span className="text-danger">*</span>
                          </InputLabel>
                          <Select
                            {...field}
                            labelId="countyLabel"
                            label="County"
                            size="small"
                            required={heldForCounty}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 300
                                },
                              },
                            }}
                            onChange={(e) => {
                              field.onChange(e);
                              onCountyChange(
                                e as React.ChangeEvent<{ value: unknown }>
                              );
                            }}
                          >
                            <MenuItem value="" disabled>
                              Select...
                            </MenuItem>
                            {countyOptions
                              .slice()
                              .sort((a: any, b: any) => a.countyname.localeCompare(b.countyname))
                              .map((option: any) => (
                              <MenuItem
                                key={option.countyid}
                                value={option.countyid}
                              >
                                {option.countyname}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </div>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <div>
                    <div className="form-label">
                      <label>
                        List current medications, dosages and frequency taken
                        (if any):
                      </label>
                    </div>
                    <Controller
                      name="medications"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          margin="normal"
                          type="text"
                          rows={5}
                          multiline
                          className="m-auto"
                          fullWidth
                        />
                      )}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <div>
                    <div className="form-label">
                      <label>
                        Any additional information that you believe for our
                        provider to know / Preferred Pharmacy:
                      </label>
                    </div>
                    <Controller
                      name="additionalinfo"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          margin="normal"
                          type="text"
                          rows={5}
                          multiline
                          className="m-auto"
                          fullWidth
                        />
                      )}
                    />
                  </div>
                </Grid>
              </Grid>
            </div>
          </Card.Body>
          <Card.Footer className="text-center">
            <Button type="submit" variant="contained" sx={{ minWidth: 120 }}>
              {BUTTONS.NEXT}
            </Button>
          </Card.Footer>
        </Card>
      </div>
    </form>
  );
};

export default InmateInfoForm;
